import {
  useController,
  type Control,
  type FieldValues,
  type FieldPath,
  type RegisterOptions,
} from "react-hook-form";
import { useTheme } from "../../contexts/ThemeContext";

type Option = { value: string; label: string };

type Props<T extends FieldValues> = {
  control: Control<T>;
  name: FieldPath<T>;
  label?: string;
  options: Option[];
  placeholder?: string;
  rules?: RegisterOptions<T, FieldPath<T>>;
};

/** Liste déroulante factorisée, branchée sur react-hook-form et le thème `t`. */
export function SelectField<T extends FieldValues>({
  control, name, label, options, placeholder, rules,
}: Props<T>) {
  const { t } = useTheme();
  const { field, fieldState } = useController({ control, name, rules });
  const error = fieldState.error?.message;

  return (
    <div>
      {label && (
        <label
          htmlFor={field.name}
          style={{ display: "block", fontSize: 12, color: t.text2, marginBottom: 6, fontWeight: 500 }}
        >
          {label}{rules?.required && <span style={{ color: t.lateColor }}> *</span>}
        </label>
      )}
      <select
        id={field.name}
        name={field.name}
        ref={field.ref}
        value={field.value ?? ""}
        onChange={field.onChange}
        onBlur={field.onBlur}
        style={{
          width: "100%", padding: "10px 12px", borderRadius: 8,
          border: `1px solid ${error ? t.lateColor : t.inputBorder}`,
          background: t.inputBg, color: t.text1,
          fontSize: 14, fontFamily: "inherit", outline: "none",
        }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {error && (
        <div style={{ fontSize: 11.5, color: t.lateColor, marginTop: 5 }}>{error}</div>
      )}
    </div>
  );
}
